import { Routes } from '@angular/router';
import { PageName } from '~app/app/~shared/models/shared.models';
import { AuthLayoutComponent } from './~shared/layouts/auth-layout/auth-layout.component';
import { SignInComponent } from './auth/pages/sign-in/sign-in.component';
import { ContentWrapperComponent } from './~shared/layouts/content-wrapper/content-wrapper.component';
import { AdministratorsComponent } from './settings/pages/administrators/administrators.component';
import { PasswordInitComponent } from './auth/pages/password-init/password-init.component';
import { SecurityComponent } from './settings/pages/security/security.component';
import { PasswordResetComponent } from './auth/pages/password-reset/password-reset.component';
import { ProfileComponent } from './profile/pages/profile/profile.component';
import { AUTHENTICATED_GUARD } from './~shared/guards/authenticated.guard';
import { NON_AUTHENTICATED_GUARD } from './~shared/guards/non-authenticated.guard';
import { NotFoundComponent } from './not-found/not-found.component';
import { CustomerLayoutComponent } from './~shared/layouts/customer-layout/customer-layout.component';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { UserRole } from '~core/models/auth.models';
import { ScreenListPageComponent } from './screen/pages/screen-list-page/screen-list-page.component';
import { BrandListPageComponent } from './brand/pages/brand-list-page/brand-list-page.component';
import { AdvertiserGroupListPageComponent } from './advertiser-group/pages/advertiser-group-list-page/advertiser-group-list-page.component';
import { AdvertiserSubSectorListPageComponent } from './advertiser-sub-sector/pages/advertiser-sub-sector-list-page/advertiser-sub-sector-list-page.component';
import { AdvertiserSectorListPageComponent } from './advertiser-sector/pages/advertiser-sector-list-page/advertiser-sector-list-page.component';
import { AdvertiserListPageComponent } from './advertiser/pages/advertiser-list-page/advertiser-list-page.component';
import { UserAdsListPageComponent } from '~app/user-ads/pages/user-ads-list-page/user-ads-list-page.component';
import { LocationListPageComponent } from './location/pages/location/location-list-page.component';
import { ChannelListPageComponent } from './channel/pages/channel-list/channel-list-page.component';
import { AdsExpensesListPageComponent } from './ads-expenses/pages/ads-expenses-list-page/ads-expenses-list-page.component';
import { CompetingAdsListPageComponent } from './competing-ads/pages/competing-ads-list-page/competing-ads-list-page.component';
import { CompetingTimeSlotsListPageComponent } from './competing-time-slots/pages/competing-time-slots-list-page/competing-time-slots-list-page.component';
import { AdsTypeListPageComponent } from './ads-type/pages/ads-type-list-page/ads-type-list-page.component';
import { sectorsResolver } from './~shared/services/tag-service.resolver';
import { SUBSCRIPTION_GUARD } from './~shared/guards/subscription.guard';
import { SubscriberAdvertiserListPageComponent } from '~app/app/subscriber-advertiser/pages/subscriber-advertiser-list-page/subscriber-advertiser-list-page.component';
import { SubscriberAdsListPageComponent } from './subscriber-ads/pages/subscriber-ads-list-page/subscriber-ads-list-page.component';
import { SubscriberChannelListPageComponent } from './subscriber-channel/pages/subscriber-channel-list-page/subscriber-channel-list-page.component';
import { SubscriberAdvertiserDetailsPageComponent } from './subscriber-advertiser/pages/subscriber-advertiser-details-page/subscriber-advertiser-details-page.component';

export const routes: Routes = [
  {
    path: 'signin',
    canActivate: [NON_AUTHENTICATED_GUARD],
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        data: {
          pageName: PageName.signIn
        },
        component: SignInComponent
      }
    ]
  },
  {
    path: 'signup',
    canActivate: [NON_AUTHENTICATED_GUARD],
    component: CustomerLayoutComponent,
    resolve: {
      sectors: sectorsResolver
    },
    children: [
      {
        path: '',
        data: {
          pageName: PageName.signUp
        },
        loadComponent: () =>
          import('./auth/pages/sign-up/sign-up.component').then(
            m => m.SignUpComponent
          )
      }
    ]
  },
  {
    path: 'subscriptions',
    canActivate: [SUBSCRIPTION_GUARD],
    component: CustomerLayoutComponent,
    children: [
      {
        path: '',
        data: {
          pageName: PageName.subscriptions
        },
        loadComponent: () =>
          import(
            './subscription/pages/subscriptions/subscriptions.component'
          ).then(m => m.SubscriptionsComponent)
      }
    ]
  },
  {
    path: 'password/change',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        data: {
          pageName: PageName.passwordChange
        },
        component: PasswordInitComponent
      }
    ]
  },
  {
    path: 'password/resetting/request',
    canActivate: [NON_AUTHENTICATED_GUARD],
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        data: {
          pageName: PageName.passwordChange
        },
        component: PasswordResetComponent
      }
    ]
  },
  {
    path: '',
    component: ContentWrapperComponent,
    canActivate: [AUTHENTICATED_GUARD],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./dashboard/pages/dashboard/dashboard.component').then(
            m => m.DashboardComponent
          ),
        data: {
          pageName: PageName.dashboard
        }
      },
      {
        path: 'ads',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              UserRole.ROLE_ADMIN,
              UserRole.ROLE_SUPER_ADMIN,
              UserRole.ROLE_SUBSCRIBER
            ],
            except: [UserRole.ROLE_ANALYST],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'list',
            loadComponent: () =>
              import('./ads/pages/ads-list/ads-list.component').then(
                m => m.AdsListComponent
              ),
            data: {
              pageName: PageName.adsList,
              headerTitle: 'Publicités'
            }
          },
          {
            path: 'list/:id',
            loadComponent: () =>
              import('./ads/pages/ads-details/ads-details.component').then(
                m => m.AdsDetailsComponent
              ),
            data: {
              pageName: PageName.advertisingDetails
            }
          },
          {
            path: 'types',
            component: AdsTypeListPageComponent,
            data: {
              pageName: PageName.adsTypes
            }
          },
          {
            path: 'formats',
            loadComponent: () =>
              import(
                './format/pages/format-list-page/format-list-page.component'
              ).then(m => m.FormatListPageComponent),
            data: {
              pageName: PageName.adsFormats,
              headerTitle: 'Formats'
            }
          },
          {
            path: 'prices',
            loadComponent: () =>
              import(
                './ads-price/pages/ads-price-list-page/ads-price-list-page.component'
              ).then(m => m.AdsPriceListPageComponent),
            data: {
              pageName: PageName.adsPrice,
              headerTitle: 'Tarifs',
              permissions: {
                only: [UserRole.ROLE_ADMIN, UserRole.ROLE_SUPER_ADMIN]
              }
            }
          },
          {
            path: 'my-ads',
            component: UserAdsListPageComponent,
            data: {
              pageName: PageName.userAds,
              headerTitle: 'Mes publicités',
              permissions: {
                except: [UserRole.ROLE_ADMIN, UserRole.ROLE_SUPER_ADMIN]
              }
            }
          },
          {
            path: 'competing-ads',
            component: CompetingAdsListPageComponent,
            data: {
              pageName: PageName.competingAds,
              headerTitle: 'Publicités concurrentes',
              permissions: {
                except: [UserRole.ROLE_ADMIN, UserRole.ROLE_SUPER_ADMIN]
              }
            }
          },
          {
            path: 'competing-time-slots',
            component: CompetingTimeSlotsListPageComponent,
            data: {
              pageName: PageName.competingTimeSlots,
              headerTitle: 'Tranches horaires concurrentes',
              permissions: {
                except: [UserRole.ROLE_ADMIN, UserRole.ROLE_SUPER_ADMIN]
              }
            }
          },
          {
            path: 'ads-expenses',
            component: AdsExpensesListPageComponent,
            data: {
              pageName: PageName.adsExpenses,
              headerTitle: 'Dépenses publicitaire',
              permissions: {
                except: [UserRole.ROLE_ADMIN, UserRole.ROLE_SUPER_ADMIN]
              }
            }
          }
        ]
      },
      {
        path: 'campaigns',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [UserRole.ROLE_ADMIN, UserRole.ROLE_USER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'list',
            loadComponent: () =>
              import(
                './campaign/pages/campaign-list-page/campaign-list-page.component'
              ).then(m => m.CampaignListPageComponent),
            data: {
              pageName: PageName.campaignsList,
              headerTitle: 'Liste des campagnes'
            }
          },
          {
            path: 'messages',
            loadComponent: () =>
              import(
                './message/pages/message-list-page/message-list-page.component'
              ).then(m => m.MessageListPageComponent),
            data: {
              pageName: PageName.campaignsMessages,
              headerTitle: 'Messages'
            }
          }
        ]
      },
      {
        path: 'advertisers',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              UserRole.ROLE_SUPER_ADMIN,
              UserRole.ROLE_ADMIN,
              UserRole.ROLE_USER
            ],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'list',
            component: AdvertiserListPageComponent,
            data: {
              pageName: PageName.advertisersList,
              headerTitle: 'Annonceurs'
            }
          },
          {
            path: 'brands',
            component: BrandListPageComponent,
            data: {
              pageName: PageName.advertisersBrands,
              headerTitle: 'Marques'
            }
          },
          {
            path: 'groups',
            component: AdvertiserGroupListPageComponent,
            data: {
              pageName: PageName.advertisersGroups,
              headerTitle: 'Groupes'
            }
          },
          {
            path: 'sectors',
            component: AdvertiserSectorListPageComponent,
            data: {
              pageName: PageName.advertisersSectors,
              headerTitle: 'Secteurs'
            }
          },
          {
            path: 'sub-sectors',
            component: AdvertiserSubSectorListPageComponent,
            data: {
              pageName: PageName.advertisersSubSectors,
              headerTitle: 'Sous-secteurs'
            }
          }
        ]
      },
      {
        path: 'medias',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              UserRole.ROLE_SUPER_ADMIN,
              UserRole.ROLE_ADMIN,
              UserRole.ROLE_USER
            ],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'screens',
            component: ScreenListPageComponent,
            data: {
              pageName: PageName.mediasScreens,
              headerTitle: 'Ecrans'
            }
          },
          {
            path: 'supports',
            component: ChannelListPageComponent,
            data: {
              pageName: PageName.mediasSupports,
              headerTitle: 'Supports'
            }
          },
          {
            path: 'locations',
            component: LocationListPageComponent,
            data: {
              pageName: PageName.mediasLocations,
              headerTitle: 'Emplacements'
            }
          }
        ]
      },
      {
        path: 'subscription-plans',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [UserRole.ROLE_SUPER_ADMIN, UserRole.ROLE_ADMIN],
            redirectTo: '/'
          },
          pageName: PageName.subscriptionsPlans,
          headerTitle: "Formules d'abonnements"
        },
        loadComponent: () =>
          import(
            './subscription-plan/pages/subscription-plan-list-page/subscription-plan-list-page.component'
          ).then(m => m.SubscriptionPlanListPageComponent)
      },
      {
        path: 'subscribers',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [UserRole.ROLE_SUPER_ADMIN, UserRole.ROLE_ADMIN],
            redirectTo: '/'
          },
          pageName: PageName.subscribers,
          headerTitle: 'Clients'
        },
        loadComponent: () =>
          import(
            './subscriber/pages/subscriber-list-page/subscriber-list-page.component'
          ).then(m => m.SubscriberListPageComponent)
      },
      {
        path: 'settings',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              UserRole.ROLE_ADMIN,
              UserRole.ROLE_USER,
              UserRole.ROLE_SUPER_ADMIN
            ],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'security',
            component: SecurityComponent,
            data: {
              pageName: PageName.settings
            }
          },
          {
            path: 'administrators',
            component: AdministratorsComponent,
            data: {
              pageName: PageName.admins,
              headerTitle: 'Utilisateurs et rôles'
            }
          }
        ]
      },
      {
        path: 'profile',
        component: ProfileComponent,
        data: {
          pageName: PageName.profile
        }
      },

      // analyst
      {
        path: 'analyst',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [UserRole.ROLE_ANALYST],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'ads'
          },
          {
            path: 'ads',
            loadComponent: () =>
              import(
                './analyst-ads/pages/analyst-ads-list-page/analyst-ads-list-page.component'
              ).then(m => m.AnalystAdsListPageComponent),
            data: {
              pageName: PageName.analystAdsList,
              headerTitle: 'Publicités'
            }
          },
          {
            path: 'ads/:id/tracks',
            loadComponent: () =>
              import(
                './analyst-ads/pages/analyst-ads-tracks/analyst-ads-tracks-page.component'
              ).then(m => m.AnalystAdsTracksDetailsPageComponent),
            data: {
              pageName: PageName.analystAdsTracks,
              headerTitle: 'Historique de(s) constat(s)'
            }
          }
        ]
      },

      //subscriber
      {
        path: 'subscriber',
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [UserRole.ROLE_SUBSCRIBER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'advertisers',
            component: SubscriberAdvertiserListPageComponent,
            data: {
              pageName: PageName.subscriberAdvertisersList,
              headerTitle: 'Annonceurs'
            }
          },
          {
            path: 'advertisers/:id/stats',
            component: SubscriberAdvertiserDetailsPageComponent,
            data: {
              pageName: PageName.subscriberAdvertisersDetails
            }
          },
          {
            path: 'ads',
            component: SubscriberAdsListPageComponent,
            data: {
              pageName: PageName.subscriberAdsList,
              headerTitle: 'Publicités'
            }
          },
          {
            path: 'channels',
            component: SubscriberChannelListPageComponent,
            data: {
              pageName: PageName.subscriberChannelsList,
              headerTitle: 'Supports'
            }
          }
        ]
      },
      {
        path: '**',
        component: NotFoundComponent
      }
    ]
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];
